import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import api from '../../../lib/api';

export default function TrainerStats() {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('members');
  const [currentMonth] = useState(new Date()?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));

  const fetchTrainerStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [staffRes, membersRes] = await Promise.all([
        api.get('/staff'),
        api.get('/members', { params: { limit: 2000 } }),
      ]);
      const trainerProfiles = staffRes?.data || [];
      const allMembers = membersRes?.data || [];

      const stats = trainerProfiles?.map(trainer => {
        const memberCount = allMembers?.filter(m => m?.trainer_id === trainer?.id)?.length;
        return {
          id: trainer?.id,
          name: trainer?.full_name,
          role: trainer?.role,
          members: memberCount,
          checkIns: 0,
          totalEarned: 0,
          pendingAmount: 0,
          paidAmount: 0,
        };
      });

      setTrainers(stats);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrainerStats();
  }, [fetchTrainerStats]);

  const sorted = [...trainers]?.sort((a, b) => {
    if (sortBy === 'members') return b?.members - a?.members;
    if (sortBy === 'checkins') return b?.checkIns - a?.checkIns;
    if (sortBy === 'commission') return b?.totalEarned - a?.totalEarned;
    return 0;
  });

  const totalMembers = trainers?.reduce((s, t) => s + t?.members, 0);
  const totalCheckIns = trainers?.reduce((s, t) => s + t?.checkIns, 0);
  const totalCommission = trainers?.reduce((s, t) => s + t?.totalEarned, 0);
  const totalPending = trainers?.reduce((s, t) => s + t?.pendingAmount, 0);

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">Trainer Performance & Commission</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] bg-[var(--color-muted)] px-2 py-1 rounded">{currentMonth}</span>
          <select
            value={sortBy}
            onChange={e => setSortBy(e?.target?.value)}
            className="px-2 py-1.5 bg-[var(--color-background)] border border-[var(--color-border)] rounded text-xs text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
          >
            <option value="members">Sort: Members</option>
            <option value="checkins">Sort: Check-ins</option>
            <option value="commission">Sort: Commission</option>
          </select>
        </div>
      </div>
      {/* KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Total Trainers', value: trainers?.length, icon: 'Users', color: 'var(--color-primary)' },
          { label: 'Members Assigned', value: totalMembers, icon: 'UserCheck', color: 'var(--color-success)' },
          { label: 'Total Check-ins', value: totalCheckIns, icon: 'Activity', color: 'var(--color-secondary)' },
          { label: 'Pending Commission', value: `$${totalPending?.toLocaleString()}`, icon: 'DollarSign', color: 'var(--color-warning)' },
        ]?.map(kpi => (
          <div key={kpi?.label} className="bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-[var(--radius-sm)] flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${kpi?.color}18` }}>
              <Icon name={kpi?.icon} size={18} color={kpi?.color} />
            </div>
            <div>
              <p className="text-lg font-bold text-[var(--color-foreground)] font-[var(--font-heading)] leading-none">{kpi?.value}</p>
              <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">{kpi?.label}</p>
            </div>
          </div>
        ))}
      </div>
      {error && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-[var(--radius-sm)]">
          <Icon name="AlertCircle" size={16} color="var(--color-error)" />
          <p className="text-sm text-[var(--color-error)] font-[var(--font-caption)]">{error}</p>
        </div>
      )}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4]?.map(i => <div key={i} className="h-40 bg-[var(--color-muted)] rounded animate-pulse" />)}
        </div>
      ) : sorted?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <Icon name="Users" size={32} color="var(--color-muted-foreground)" />
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No trainer data available</p>
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Staff and trainer profiles will appear here</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {sorted?.map((t) => {
            const commissionGoal = Math.max(t?.totalEarned * 1.2, 1000);
            const commissionPct = Math.round((t?.totalEarned / commissionGoal) * 100);
            return (
              <div key={t?.id} className="bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] p-4 flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-[var(--color-primary)] font-[var(--font-caption)]">
                      {t?.name?.split(' ')?.map(n => n?.[0])?.join('') || '?'}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] truncate">{t?.name}</p>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--color-muted)] text-[var(--color-muted-foreground)] font-[var(--font-caption)] capitalize">{t?.role}</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Members', value: t?.members, icon: 'Users' },
                    { label: 'Check-ins', value: t?.checkIns, icon: 'Activity' },
                    { label: 'Pending', value: `$${t?.pendingAmount?.toLocaleString()}`, icon: 'Clock' },
                  ]?.map((stat) => (
                    <div key={stat?.label} className="text-center">
                      <p className="text-sm font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">{stat?.value}</p>
                      <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{stat?.label}</p>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Total Earned</span>
                    <span className="text-sm font-bold text-[var(--color-success)] font-[var(--font-caption)]">${t?.totalEarned?.toLocaleString()}</span>
                  </div>
                  <div className="h-1.5 bg-[var(--color-muted)] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${Math.min(commissionPct, 100)}%`, backgroundColor: commissionPct >= 80 ? 'var(--color-success)' : 'var(--color-warning)' }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{commissionPct}% of goal</p>
                    <p className="text-[10px] text-[var(--color-success)] font-[var(--font-caption)]">Paid: ${t?.paidAmount?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
